// geoMath.js — sitrep payload-shape overhaul (MINSKY-16404 design review): deterministic
// haversine distance/bearing/compass-direction utility.
//
// Why this exists: the design review (see buildSitrepPayload.js's module doc comment) flags that
// LLMs are "generally more reliable at real-world geographic reasoning than at geometric
// computation" (GPSBench 2026) — i.e. asking a model to compute distance/bearing from two raw
// lat/lon pairs risks a hallucinated number in the final report. Precomputing these fields here,
// deterministically, removes that risk entirely for the common "unit moved" case. dtgDiff.js's
// "moved" events use this for their distanceKm/bearingDeg/direction fields (with the marking's own
// SIDC-encoded Z_SPEED/Q_DIRECTION_OF_MOVEMENT modifiers, if present, surfacing alongside as the
// analyst-entered ground truth — see sidcDecoder.js).
//
// Pure, no OL/DOM dependency — every function here takes/returns plain numbers so it's trivially
// unit-testable and reusable outside the map editor.

const EARTH_RADIUS_KM = 6371;

function toRadians(deg) {
    return (deg * Math.PI) / 180;
}

function toDegrees(rad) {
    return (rad * 180) / Math.PI;
}

/**
 * Great-circle distance between two lat/lon points, in kilometers (spherical-earth
 * approximation — more than accurate enough for a sitrep's "unit moved ~N km" narrative).
 *
 * @param {number} fromLat
 * @param {number} fromLon
 * @param {number} toLat
 * @param {number} toLon
 * @returns {number} distance in kilometers, rounded to 2 decimals.
 */
export function haversineDistanceKm(fromLat, fromLon, toLat, toLon) {
    const dLat = toRadians(toLat - fromLat);
    const dLon = toRadians(toLon - fromLon);
    const lat1 = toRadians(fromLat);
    const lat2 = toRadians(toLat);

    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(EARTH_RADIUS_KM * c * 100) / 100;
}

/**
 * Initial great-circle bearing from one lat/lon point to another, in degrees clockwise from
 * true north (0 = N, 90 = E, 180 = S, 270 = W).
 *
 * @param {number} fromLat
 * @param {number} fromLon
 * @param {number} toLat
 * @param {number} toLon
 * @returns {number} bearing in degrees, rounded to the nearest whole degree, within [0, 360).
 */
export function bearingDeg(fromLat, fromLon, toLat, toLon) {
    const lat1 = toRadians(fromLat);
    const lat2 = toRadians(toLat);
    const dLon = toRadians(toLon - fromLon);

    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    const raw = toDegrees(Math.atan2(y, x));
    return Math.round((raw + 360) % 360);
}

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/**
 * Bucket a bearing (degrees clockwise from north) into one of the 8 compass directions.
 * @param {number} bearing - degrees, any real value (wrapped into [0, 360) first).
 * @returns {'N'|'NE'|'E'|'SE'|'S'|'SW'|'W'|'NW'}
 */
export function compassDirection(bearing) {
    const normalized = ((bearing % 360) + 360) % 360;
    const index = Math.round(normalized / 45) % 8;
    return COMPASS_POINTS[index];
}

/**
 * Convenience wrapper bundling distance + bearing + compass direction for a "moved" event.
 *
 * @param {number} fromLat
 * @param {number} fromLon
 * @param {number} toLat
 * @param {number} toLon
 * @returns {{distanceKm: number, bearingDeg: number, direction: string}}
 */
export function computeMovement(fromLat, fromLon, toLat, toLon) {
    const distanceKm = haversineDistanceKm(fromLat, fromLon, toLat, toLon);
    const bearing = bearingDeg(fromLat, fromLon, toLat, toLon);
    return {
        distanceKm,
        bearingDeg: bearing,
        direction: compassDirection(bearing),
    };
}
